import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { DatePicker, Select, Space } from 'antd';
import { Bar, Column, Line, Pie } from '@ant-design/charts';
import dayjs from 'dayjs';
import StatusBadge from './components/StatusBadge.js';

const { RangePicker } = DatePicker;

const statCardStyle = {
  backgroundColor: 'white',
  padding: '20px',
  borderRadius: '10px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
  minWidth: '190px',
  flex: '1 1 220px',
  margin: '10px'
};

const formatINR = (value) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0
}).format(value);

const formatCompactINR = (value) => {
  const amount = Number(value) || 0;
  const absAmount = Math.abs(amount);

  if (absAmount >= 10000000) {
    const formatted = amount / 10000000;
    return `${Number.isInteger(formatted) ? formatted.toFixed(0) : formatted.toFixed(1)}cr`;
  }

  if (absAmount >= 100000) {
    const formatted = amount / 100000;
    return `${Number.isInteger(formatted) ? formatted.toFixed(0) : formatted.toFixed(1)}L`;
  }

  if (absAmount >= 1000) {
    const formatted = amount / 1000;
    return `${Number.isInteger(formatted) ? formatted.toFixed(0) : formatted.toFixed(1)}k`;
  }

  return `${Math.round(amount)}`;
};

const toFiniteNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatPeriodChange = (current, previous) => {
  if (previous === null || previous === undefined) {
    return { text: 'vs previous period: N/A', tone: 'neutral' };
  }

  if (previous === 0) {
    if (current === 0) {
      return { text: 'No change vs previous period', tone: 'neutral' };
    }

    return { text: 'New vs previous period', tone: 'positive' };
  }

  const diff = current - previous;
  const pct = (diff / previous) * 100;

  return {
    text: `${diff >= 0 ? '+' : ''}${pct.toFixed(1)}% vs previous period`,
    tone: diff >= 0 ? 'positive' : 'negative'
  };
};

const AdminDashboard = ({ db }) => {
  const [salesData, setSalesData] = useState({ quotes: [], invoices: [], orders: [] });
  const [salespersons, setSalespersons] = useState([]);
  const [salespersonFilter, setSalespersonFilter] = useState('all');
  const [period, setPeriod] = useState('this_month');
  const [orderChartView, setOrderChartView] = useState('daily');
  const [quoteChartView, setQuoteChartView] = useState('day');
  const [customRange, setCustomRange] = useState({
    startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
    endDate: dayjs().endOf('month').format('YYYY-MM-DD')
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSalesData = async () => {
      if (!db) return;
      setLoading(true);
      setError(null);

      try {
        const [quotesSnap, invoicesSnap, ordersSnap] = await Promise.all([
          getDocs(collection(db, 'quotes')),
          getDocs(collection(db, 'invoices')),
          getDocs(collection(db, 'orders'))
        ]);

        const quotes = quotesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const invoices = invoicesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const orders = ordersSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        const salesPersonSet = new Set();
        orders.forEach((order) => {
          if (order.salesPerson) salesPersonSet.add(order.salesPerson);
        });
        quotes.forEach((quote) => {
          if (quote.salesPerson) salesPersonSet.add(quote.salesPerson);
        });
        invoices.forEach((invoice) => {
          if (invoice.salesPerson) salesPersonSet.add(invoice.salesPerson);
        });

        setSalespersons(Array.from(salesPersonSet).sort());
        setSalesData({ quotes, invoices, orders });
      } catch (fetchError) {
        console.error('Failed to load sales dashboard data', fetchError);
        setError('Unable to load sales metrics. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSalesData();
  }, [db]);

  const parseItemDate = (item) => {
    const dateValue = item.date || item.createdAt;
    if (!dateValue) return null;
    if (dateValue instanceof Date) return dateValue;
    if (dateValue?.toDate) return dateValue.toDate();
    return new Date(dateValue);
  };

  const getPeriodRange = () => {
    const today = dayjs();

    if (period === 'today') {
      return { start: today.startOf('day'), end: today.endOf('day') };
    }

    if (period === 'this_week') {
      return { start: today.startOf('week'), end: today.endOf('week') };
    }

    if (period === 'this_month') {
      return { start: today.startOf('month'), end: today.endOf('month') };
    }

    if (period === 'this_quarter') {
      const quarterStartMonth = Math.floor(today.month() / 3) * 3;
      const start = dayjs(new Date(today.year(), quarterStartMonth, 1)).startOf('day');
      const end = dayjs(new Date(today.year(), quarterStartMonth + 3, 0)).endOf('day');
      return { start, end };
    }

    if (period === 'this_year') {
      return { start: today.startOf('year'), end: today.endOf('year') };
    }

    if (period === 'custom_range') {
      const startDate = customRange.startDate ? dayjs(customRange.startDate).startOf('day') : null;
      const endDate = customRange.endDate ? dayjs(customRange.endDate).endOf('day') : null;
      return { start: startDate, end: endDate };
    }

    return { start: today.startOf('day'), end: today.endOf('day') };
  };

  const getPresetRange = (selectedPeriod) => {
    const now = dayjs();
    let start = now;
    let end = now;

    if (selectedPeriod === 'today') {
      start = now;
      end = now;
    } else if (selectedPeriod === 'this_week') {
      start = now.startOf('week');
      end = now.endOf('week');
    } else if (selectedPeriod === 'this_month') {
      start = now.startOf('month');
      end = now.endOf('month');
    } else if (selectedPeriod === 'this_quarter') {
      const quarterStartMonth = Math.floor(now.month() / 3) * 3;
      const startDate = new Date(now.year(), quarterStartMonth, 1);
      const endDate = new Date(now.year(), quarterStartMonth + 3, 0);
      start = dayjs(startDate);
      end = dayjs(endDate);
    } else if (selectedPeriod === 'this_year') {
      start = now.startOf('year');
      end = now.endOf('year');
    }

    return {
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD')
    };
  };

  const handlePeriodChange = (value) => {
    setPeriod(value);
    if (value !== 'custom_range') {
      setCustomRange(getPresetRange(value));
    }
  };

  const getPreviousPeriodRange = (currentStart, currentEnd) => {
    if (!currentStart || !currentEnd) return { start: null, end: null };

    if (period === 'today') {
      return {
        start: currentStart.subtract(1, 'day').startOf('day'),
        end: currentEnd.subtract(1, 'day').endOf('day')
      };
    }

    if (period === 'this_week') {
      const previousStart = currentStart.subtract(1, 'week').startOf('week');
      return {
        start: previousStart,
        end: previousStart.endOf('week')
      };
    }

    if (period === 'this_month') {
      const previousStart = currentStart.subtract(1, 'month').startOf('month');
      return {
        start: previousStart,
        end: previousStart.endOf('month')
      };
    }

    if (period === 'this_quarter') {
      const previousStart = currentStart.subtract(3, 'month').startOf('quarter');
      return {
        start: previousStart,
        end: previousStart.endOf('quarter')
      };
    }

    if (period === 'this_year') {
      const previousStart = currentStart.subtract(1, 'year').startOf('year');
      return {
        start: previousStart,
        end: previousStart.endOf('year')
      };
    }

    if (period === 'custom_range') {
      const daysInRange = currentEnd.startOf('day').diff(currentStart.startOf('day'), 'day') + 1;
      if (!Number.isFinite(daysInRange) || daysInRange <= 0) {
        return { start: null, end: null };
      }

      return {
        start: currentStart.subtract(daysInRange, 'day').startOf('day'),
        end: currentStart.subtract(1, 'day').endOf('day')
      };
    }

    return { start: null, end: null };
  };

  const { start: filterStart, end: filterEnd } = getPeriodRange();
  const { start: previousFilterStart, end: previousFilterEnd } = getPreviousPeriodRange(filterStart, filterEnd);

  const matchesSalesperson = (item) => {
    return salespersonFilter === 'all' || (item.salesPerson || '').toLowerCase() === salespersonFilter.toLowerCase();
  };

  const matchesFilters = (item) => {
    if (!matchesSalesperson(item)) return false;

    const itemDate = parseItemDate(item);
    if (!itemDate || (!filterStart && !filterEnd)) return true;

    const fromMatch = filterStart ? itemDate >= filterStart : true;
    const toMatch = filterEnd ? itemDate <= filterEnd : true;
    return fromMatch && toMatch;
  };

  const matchesPreviousFilters = (item) => {
    if (!matchesSalesperson(item)) return false;

    const itemDate = parseItemDate(item);
    if (!itemDate || (!previousFilterStart && !previousFilterEnd)) return false;

    const fromMatch = previousFilterStart ? itemDate >= previousFilterStart.toDate() : true;
    const toMatch = previousFilterEnd ? itemDate <= previousFilterEnd.toDate() : true;
    return fromMatch && toMatch;
  };

  const filteredQuotes = salesData.quotes.filter(matchesFilters);
  const filteredInvoices = salesData.invoices.filter(matchesFilters);
  const filteredOrders = salesData.orders.filter(matchesFilters);
  const previousFilteredQuotes = salesData.quotes.filter(matchesPreviousFilters);
  const previousFilteredInvoices = salesData.invoices.filter(matchesPreviousFilters);
  const previousFilteredOrders = salesData.orders.filter(matchesPreviousFilters);

  const summarizeOrders = (orders) => {
    const totalOrderCount = orders.length;
    const totalOrderAmount = orders.reduce((sum, order) => {
      const orderAmount = toFiniteNumber(order.orderValue ?? order.grandTotal ?? order.totalAmount);
      return sum + orderAmount;
    }, 0);
    const deliveredCount = orders.filter((order) => String(order.status).toLowerCase() === 'delivered').length;
    const rentalCount = orders.filter((order) => String(order.orderType).toLowerCase() === 'rental').length;
    const salesCount = orders.filter((order) => String(order.orderType).toLowerCase() === 'sales').length;

    return {
      totalOrderCount,
      totalOrderAmount,
      averageOrderValue: totalOrderCount > 0 ? totalOrderAmount / totalOrderCount : 0,
      deliveredCount,
      rentalCount,
      salesCount
    };
  };

  const currentOrderSummary = summarizeOrders(filteredOrders);
  const previousOrderSummary = summarizeOrders(previousFilteredOrders);

  const summarizeDocuments = (documents) => {
    const totalCount = documents.length;
    const totalAmount = documents.reduce((sum, item) => sum + (Number(item.grandTotal) || 0), 0);

    return {
      totalCount,
      totalAmount,
      averageAmount: totalCount > 0 ? totalAmount / totalCount : 0
    };
  };

  const currentQuoteSummary = summarizeDocuments(filteredQuotes);
  const previousQuoteSummary = summarizeDocuments(previousFilteredQuotes);
  const currentInvoiceSummary = summarizeDocuments(filteredInvoices);
  const previousInvoiceSummary = summarizeDocuments(previousFilteredInvoices);

  const invoicesByCustomer = filteredInvoices.reduce((acc, invoice) => {
    const customer = invoice.customerName || invoice.firmName || invoice.customer || 'Unknown';
    const invoiceAmount = toFiniteNumber(invoice.grandTotal);
    const salesperson = invoice.salesPerson || invoice.salesperson || invoice.salespersonName || 'Unassigned';
    const documentType = invoice.invoiceType || invoice.orderType || invoice.type || 'Invoice';

    if (!acc[customer]) {
      acc[customer] = {
        customer,
        amount: 0,
        salesperson,
        documentType,
        highestDocumentAmount: 0
      };
    }

    acc[customer].amount += invoiceAmount;

    if (invoiceAmount >= acc[customer].highestDocumentAmount) {
      acc[customer].salesperson = salesperson;
      acc[customer].documentType = documentType;
      acc[customer].highestDocumentAmount = invoiceAmount;
    }

    return acc;
  }, {});
  const topCustomersByInvoiceValue = Object.values(invoicesByCustomer)
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 5);

  const quotesByCustomer = filteredQuotes.reduce((acc, quote) => {
    const customer = quote.customerName || quote.firmName || quote.customer || 'Unknown';
    const quoteAmount = toFiniteNumber(quote.grandTotal);
    const salesperson = quote.salesPerson || quote.salesperson || quote.salespersonName || 'Unassigned';
    const documentType = quote.quoteType || quote.orderType || quote.type || 'Quote';

    if (!acc[customer]) {
      acc[customer] = {
        customer,
        amount: 0,
        salesperson,
        documentType,
        highestDocumentAmount: 0
      };
    }

    acc[customer].amount += quoteAmount;

    if (quoteAmount >= acc[customer].highestDocumentAmount) {
      acc[customer].salesperson = salesperson;
      acc[customer].documentType = documentType;
      acc[customer].highestDocumentAmount = quoteAmount;
    }

    return acc;
  }, {});
  const topCustomersByQuoteValue = Object.values(quotesByCustomer)
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 5);

  const ordersByCustomer = filteredOrders.reduce((acc, order) => {
    const customer = order.customerName || order.firmName || order.customer || 'Unknown';
    const orderAmount = toFiniteNumber(order.orderValue ?? order.grandTotal ?? order.totalAmount);
    const salesperson = order.salesPerson || order.salesperson || order.salespersonName || 'Unassigned';
    const orderType = order.orderType || order.type || order.category || 'Unknown type';

    if (!acc[customer]) {
      acc[customer] = {
        customer,
        amount: 0,
        salesperson,
        orderType,
        highestOrderAmount: 0
      };
    }

    acc[customer].amount += orderAmount;

    if (orderAmount >= acc[customer].highestOrderAmount) {
      acc[customer].salesperson = salesperson;
      acc[customer].orderType = orderType;
      acc[customer].highestOrderAmount = orderAmount;
    }

    return acc;
  }, {});

  const topOrderCustomers = Object.values(ordersByCustomer)
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 5);

  const recentOrders = filteredOrders
    .map((order) => {
      const orderDate = parseItemDate(order);
      const orderAmount = toFiniteNumber(order.orderValue ?? order.grandTotal ?? order.totalAmount);

      return {
        customer: order.customerName || order.firmName || order.customer || 'Unknown',
        amount: orderAmount,
        salesperson: order.salesPerson || order.salesperson || order.salespersonName || 'Unassigned',
        orderType: order.orderType || order.type || order.category || 'Unknown type',
        orderNumber: order.orderNumber || order.orderNo || order.id || 'No order number',
        dateLabel: orderDate ? dayjs(orderDate).format('DD MMM YYYY') : 'No date',
        status: order.status || order.packingStatus || '',
        sortDate: orderDate ? orderDate.getTime() : 0
      };
    })
    .sort((a, b) => b.sortDate - a.sortDate)
    .slice(0, 5);

  const quoteInvoiceKpiStats = [
    {
      label: 'Total Quotes',
      value: currentQuoteSummary.totalCount,
      previousValue: previousQuoteSummary.totalCount,
      format: 'count'
    },
    {
      label: 'Average Quote Value',
      value: currentQuoteSummary.averageAmount,
      previousValue: previousQuoteSummary.averageAmount,
      format: 'currency'
    },
    {
      label: 'Total Invoices',
      value: currentInvoiceSummary.totalCount,
      previousValue: previousInvoiceSummary.totalCount,
      format: 'count'
    },
    {
      label: 'Average Invoice Value',
      value: currentInvoiceSummary.averageAmount,
      previousValue: previousInvoiceSummary.averageAmount,
      format: 'currency'
    }
  ];

  const orderStats = [
    { label: 'Total Orders', value: currentOrderSummary.totalOrderCount },
    { label: 'Total Order Value', value: formatINR(currentOrderSummary.totalOrderAmount) },
    { label: 'Average Order Value', value: formatINR(currentOrderSummary.averageOrderValue) },
    { label: 'Orders Delivered', value: currentOrderSummary.deliveredCount },
    { label: 'Total Rental Orders', value: currentOrderSummary.rentalCount },
    { label: 'Total Sales Orders', value: currentOrderSummary.salesCount }
  ];
  
  const orderKpiStats = [
    { label: 'Total Orders', value: currentOrderSummary.totalOrderCount, previousValue: previousOrderSummary.totalOrderCount, format: 'count' },
    { label: 'Total Rental Orders', value: currentOrderSummary.rentalCount, previousValue: previousOrderSummary.rentalCount, format: 'count' },
    { label: 'Total Sales Orders', value: currentOrderSummary.salesCount, previousValue: previousOrderSummary.salesCount, format: 'count' },
    { label: 'Orders Delivered', value: currentOrderSummary.deliveredCount, previousValue: previousOrderSummary.deliveredCount, format: 'count' },
    { label: 'Total Order Value', value: currentOrderSummary.totalOrderAmount, previousValue: previousOrderSummary.totalOrderAmount, format: 'currency' }
  ];

  const chartOrders = filteredOrders;

  const orderVolumeData = (() => {
    if (!chartOrders.length) return [];

    const buckets = new Map();
    chartOrders.forEach((order) => {
      const orderDate = parseItemDate(order);
      if (!orderDate) return;

      const isRental = String(order.orderType).toLowerCase() === 'rental';
      const isSales = String(order.orderType).toLowerCase() === 'sales';

      let key = '';
      let label = '';

      if (orderChartView === 'daily') {
        key = dayjs(orderDate).format('YYYY-MM-DD');
        label = dayjs(orderDate).format('DD MMM');
      } else if (orderChartView === 'weekly') {
        const startOfWeek = dayjs(orderDate).startOf('week').format('YYYY-MM-DD');
        key = startOfWeek;
        label = `Week of ${dayjs(startOfWeek).format('DD MMM')}`;
      } else if (orderChartView === 'monthly') {
        key = dayjs(orderDate).format('YYYY-MM');
        label = dayjs(orderDate).format('MMM YYYY');
      } else if (orderChartView === 'quarterly') {
        const quarter = Math.floor((dayjs(orderDate).month() + 3) / 3);
        key = `${dayjs(orderDate).year()}-Q${quarter}`;
        label = `${dayjs(orderDate).year()} Q${quarter}`;
      } else {
        key = dayjs(orderDate).format('YYYY');
        label = dayjs(orderDate).format('YYYY');
      }

      const bucketKey = `${key}`;
      const bucketEntry = buckets.get(bucketKey) || { label, rental: 0, sales: 0 };
      if (isRental) bucketEntry.rental += 1;
      if (isSales) bucketEntry.sales += 1;
      bucketEntry.label = label;
      buckets.set(bucketKey, bucketEntry);
    });

    return Array.from(buckets.entries())
      .map(([key, values]) => ({
        key,
        label: values.label,
        rental: values.rental,
        sales: values.sales
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  })();

  const quoteVolumeData = (() => {
    if (!filteredQuotes.length) return [];

    const buckets = new Map();
    filteredQuotes.forEach((quote) => {
      const quoteDate = parseItemDate(quote);
      if (!quoteDate) return;

      const quoteType = String(quote.orderType || quote.quoteType || '').toLowerCase();
      const isRental = quoteType === 'rental';
      const isSales = quoteType === 'sales';

      let key = '';
      let label = '';

      if (quoteChartView === 'day') {
        key = dayjs(quoteDate).format('YYYY-MM-DD');
        label = dayjs(quoteDate).format('DD MMM');
      } else if (quoteChartView === 'week') {
        const startOfWeek = dayjs(quoteDate).startOf('week').format('YYYY-MM-DD');
        key = startOfWeek;
        label = `Week of ${dayjs(startOfWeek).format('DD MMM')}`;
      } else if (quoteChartView === 'month') {
        key = dayjs(quoteDate).format('YYYY-MM');
        label = dayjs(quoteDate).format('MMM YYYY');
      } else if (quoteChartView === 'quarter') {
        const quarter = Math.floor((dayjs(quoteDate).month() + 3) / 3);
        key = `${dayjs(quoteDate).year()}-Q${quarter}`;
        label = `${dayjs(quoteDate).year()} Q${quarter}`;
      } else {
        key = dayjs(quoteDate).format('YYYY');
        label = dayjs(quoteDate).format('YYYY');
      }

      const bucketKey = `${key}`;
      const bucketEntry = buckets.get(bucketKey) || { label, rental: 0, sales: 0 };
      if (isRental) bucketEntry.rental += 1;
      if (isSales) bucketEntry.sales += 1;
      bucketEntry.label = label;
      buckets.set(bucketKey, bucketEntry);
    });

    return Array.from(buckets.entries())
      .map(([key, values]) => ({
        key,
        label: values.label,
        rental: values.rental,
        sales: values.sales
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  })();

  const quoteVolumeSeriesData = quoteVolumeData.flatMap((item) => [
    { label: item.label, type: 'Sales', value: item.sales },
    { label: item.label, type: 'Rental', value: item.rental }
  ]);

  const renderOrderCustomerList = (items, emptyMessage = 'No order customer data available.') => {
    if (items.length === 0) {
      return <div style={{ color: '#64748b' }}>{emptyMessage}</div>;
    }

    return (
      <div style={{ display: 'grid' }}>
        {items.map((item, index) => (
          <div
            key={item.customer}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              gap: '12px',
              alignItems: 'center',
              padding: index === 0 ? '2px 0 12px' : '12px 0',
              borderBottom: index === items.length - 1 ? 'none' : '1px solid #e5e7eb'
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ color: '#111827', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.customer}
              </div>
              <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.salesperson} - {item.documentType || item.orderType}
              </div>
              {(item.dateLabel || item.orderNumber) && (
                <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.dateLabel || 'No date'} - {item.orderNumber || 'No order number'}
                </div>
              )}
            </div>
            <div style={{ display: 'grid', justifyItems: 'end', gap: '6px' }}>
              <div style={{ color: '#111827', fontWeight: 700, whiteSpace: 'nowrap' }}>
                {formatINR(item.amount)}
              </div>
              {item.status ? <StatusBadge status={item.status} iconSize={12} /> : null}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const popularProductsData = (() => {
    if (!filteredQuotes.length) return [];

    const excludedSkus = new Set(['TRP1', 'TRP2']);

    const counts = filteredQuotes.reduce((acc, quote) => {
      const items = Array.isArray(quote.items) ? quote.items : [];

      items.forEach((item) => {
        const sku = String(item?.sku || item?.SKU || item?.productSku || '')
          .toUpperCase()
          .replace(/\s+/g, '');
        if (excludedSkus.has(sku)) return;

        const productName = String(item?.name || '').trim();
        if (!productName) return;
        acc[productName] = (acc[productName] || 0) + 1;
      });

      return acc;
    }, {});

    return Object.entries(counts)
      .map(([product, value]) => ({ product, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  })();

  const invoiceCountData = (() => {
    if (!filteredInvoices.length) return [];
    
    const statusCounts = filteredInvoices.reduce((acc, invoice) => {
      const status = invoice.status || 'Unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(statusCounts)
      .map(([status, count]) => ({ 
        status: String(status).trim() || 'Unknown', 
        count: Number.isFinite(count) ? count : 0 
      }))
      .filter(item => item.count > 0)
      .sort((a, b) => b.count - a.count);
  })();

  const orderStatusData = (() => {
    if (!salesData.orders.length) return [];

    const allTimeOrders = salesData.orders.filter(matchesSalesperson);

    const statusGroups = [
      { label: 'Sales Approval', statusList: ['pending_approval'] },
      { label: 'Dispatch Que', statusList: ['in_que'] },
      { label: 'Material Out Checklist', statusList: ['moc_in_progress', 'pending_ops_approval', 'pending_finance', 'moc_approved'] },
      { label: 'Packing', statusList: ['packing_queued', 'packing_scheduled'] },
      { label: 'DC and Invoice', statusList: ['dc_pending', 'invoice_pending'] },
      { label: 'Quality Check', statusList: ['invoice_ready', 'packed_ready'] },
      { label: 'Dispatched', statusList: ['in_transit'] }
    ];

    const result = statusGroups.map((group) => {
      const count = allTimeOrders.reduce((sum, order) => {
        const orderStatus = String(order.status || '').toLowerCase().trim();
        const packingStatus = String(order.packingStatus || '').toLowerCase().trim();

        const isInGroup = group.statusList.some((status) => {
          const statusLower = status.toLowerCase();
          return orderStatus === statusLower || packingStatus === statusLower;
        });

        return isInGroup ? sum + 1 : sum;
      }, 0);

      return {
        status: group.label,
        count
      };
    });

    return result;
  })();

  const topSalespersonsByOrderCount = (() => {
    const salespersonOrders = filteredOrders.reduce((acc, order) => {
      const salesperson = order.salesPerson || order.salesperson || order.salespersonName || 'Unassigned';
      acc[salesperson] = (acc[salesperson] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(salespersonOrders)
      .map(([salesperson, count]) => ({ salesperson, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  })();

  const topSalespersonsByOrderValue = (() => {
    const salespersonOrders = filteredOrders.reduce((acc, order) => {
      const salesperson = order.salesPerson || order.salesperson || order.salespersonName || 'Unassigned';
      const orderAmount = toFiniteNumber(order.orderValue ?? order.grandTotal ?? order.totalAmount);
      acc[salesperson] = (acc[salesperson] || 0) + orderAmount;
      return acc;
    }, {});

    return Object.entries(salespersonOrders)
      .map(([salesperson, amount]) => ({ salesperson, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);
  })();

  const dateRangeValue = customRange.startDate && customRange.endDate
    ? [dayjs(customRange.startDate), dayjs(customRange.endDate)]
    : [];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: 0, color: '#1e293b' }}>Admin Dashboard</h1>
        <p style={{ margin: '8px 0 0', color: '#475569' }}>Department: Sales</p>
      </div>

      <div style={{ backgroundColor: '#f8fafc', borderRadius: '16px', padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px', flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ margin: '0 0 8px', color: '#0f172a' }}>Sales Metrics</h2>
            <p style={{ margin: 0, color: '#475569' }}>Use filters to narrow the displayed numbers by salesperson and period.</p>
          </div>
          <Space wrap align="end" size={16}>
            <div style={{ minWidth: 220 }}>
              <div style={{ marginBottom: 6, color: '#475569', fontSize: 12, fontWeight: 600 }}>Salesperson</div>
              <Select
                value={salespersonFilter}
                onChange={(value) => setSalespersonFilter(value)}
                style={{ width: '100%' }}
              >
                <Select.Option value="all">All Salespersons</Select.Option>
                {salespersons.map((person) => (
                  <Select.Option key={person} value={person}>{person}</Select.Option>
                ))}
              </Select>
            </div>

            <div style={{ minWidth: 220 }}>
              <div style={{ marginBottom: 6, color: '#475569', fontSize: 12, fontWeight: 600 }}>Period</div>
              <Select
                value={period}
                onChange={handlePeriodChange}
                style={{ width: '100%' }}
              >
                <Select.Option value="today">Today</Select.Option>
                <Select.Option value="this_week">This week</Select.Option>
                <Select.Option value="this_month">This month</Select.Option>
                <Select.Option value="this_quarter">This quarter</Select.Option>
                <Select.Option value="this_year">This year</Select.Option>
                <Select.Option value="custom_range">Custom range</Select.Option>
              </Select>
            </div>

            <div style={{ minWidth: 320 }}>
              <div style={{ marginBottom: 6, color: '#475569', fontSize: 12, fontWeight: 600 }}>Custom Range</div>
              <RangePicker
                value={dateRangeValue}
                onChange={(dates) => {
                  const [start, end] = dates || [];
                  const nextRange = {
                    startDate: start ? start.format('YYYY-MM-DD') : '',
                    endDate: end ? end.format('YYYY-MM-DD') : ''
                  };
                  setCustomRange(nextRange);
                  if (period !== 'custom_range') {
                    setPeriod('custom_range');
                  }
                }}
                allowClear
                style={{ width: '100%' }}
              />
            </div>
          </Space>
        </div>

        <div style={{ marginTop: '24px' }}>
          {loading ? (
            <div style={{ color: '#334155' }}>Loading sales data...</div>
          ) : error ? (
            <div style={{ color: '#b91c1c' }}>{error}</div>
          ) : (
            <>
              <div style={{ marginBottom: '28px' }}>
                <h3 style={{ margin: '0 0 16px', color: '#0f172a' }}>Quotes & Proforma Invoices</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', margin: '-10px' }}>
                  {quoteInvoiceKpiStats.map((stat) => {
                    const periodChange = formatPeriodChange(stat.value, stat.previousValue);
                    return (
                      <div key={stat.label} style={statCardStyle}>
                        <div style={{ fontSize: '14px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '10px' }}>
                          {stat.label}
                        </div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#0f172a' }}>
                          {stat.format === 'currency' ? formatINR(stat.value) : stat.value.toLocaleString('en-IN')}
                        </div>
                        <div
                          style={{
                            marginTop: '8px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: periodChange.tone === 'positive' ? '#15803d' : periodChange.tone === 'negative' ? '#b91c1c' : '#64748b'
                          }}
                        >
                          {periodChange.text}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ marginTop: '24px', display: 'grid', gap: '20px' }}>
                  <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', alignItems: 'stretch' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0, height: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <div style={{ fontSize: '14px', color: '#475569', fontWeight: 600, whiteSpace: 'nowrap' }}>Quote Volume by Type</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                          <span style={{ color: '#64748b', fontSize: '10px', fontWeight: 600, whiteSpace: 'nowrap' }}>Group By:</span>
                          {[
                            { label: 'Day', value: 'day' },
                            { label: 'Week', value: 'week' },
                            { label: 'Month', value: 'month' },
                            { label: 'Quarter', value: 'quarter' },
                            { label: 'Year', value: 'year' }
                          ].map((option) => {
                            const active = quoteChartView === option.value;
                            return (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => setQuoteChartView(option.value)}
                                style={{
                                  border: '1px solid',
                                  borderColor: active ? '#121212' : '#cbd5e1',
                                  backgroundColor: active ? '#121212' : 'white',
                                  color: active ? 'white' : '#475569',
                                  borderRadius: '999px',
                                  padding: '2px 7px',
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  lineHeight: 1
                                }}
                              >
                                {option.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div style={{ background: 'white', padding: '16px', borderRadius: '12px', boxShadow: '0 4px 10px rgba(15, 23, 42, 0.05)', minWidth: 0, overflow: 'hidden', flex: 1, display: 'flex' }}>
                        <Line
                          data={quoteVolumeSeriesData}
                          xField="label"
                          yField="value"
                          seriesField="type"
                          color={['#8dc63f', '#121212']}
                          point={{ size: 4, shape: 'circle' }}
                          tooltip={{
                            showMarkers: true,
                            title: false,
                            items: [
                              {
                                channel: 'y',
                                name: 'Quotes',
                                valueFormatter: (value) => `${value} quotes`
                              }
                            ]
                          }}
                          height={280}
                          padding="auto"
                          autoFit
                          style={{ width: '100%', height: '100%' }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0, height: '100%' }}>
                      <div style={{ fontSize: '14px', color: '#475569', fontWeight: 600, whiteSpace: 'nowrap' }}>Popular Products</div>
                      <div style={{ background: 'white', padding: '16px', borderRadius: '12px', boxShadow: '0 4px 10px rgba(15, 23, 42, 0.05)', minWidth: 0, overflow: 'hidden', flex: 1, display: 'flex' }}>
                        <Pie
                          data={popularProductsData}
                          angleField="value"
                          colorField="product"
                          color={['#8dc63f', '#121212', '#4b5563', '#d1d5db', '#2563eb', '#f59e0b']}
                          radius={0.9}
                          innerRadius={0.6}
                          height={280}
                          autoFit
                          style={{ width: '100%', height: '100%' }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0, height: '100%' }}>
                      <div style={{ fontSize: '14px', color: '#475569', fontWeight: 600, whiteSpace: 'nowrap' }}>Customer Mix</div>
                      <div style={{ background: 'white', padding: '16px', borderRadius: '12px', boxShadow: '0 4px 10px rgba(15, 23, 42, 0.05)', minWidth: 0, overflow: 'hidden', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ color: '#64748b', fontSize: '13px' }}>Content pending.</div>
                      </div>
                    </div>

                  </div>

                  <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
                    <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 4px 10px rgba(15, 23, 42, 0.05)', overflow: 'hidden' }}>
                      <div style={{ fontSize: '14px', color: '#475569', fontWeight: 600, marginBottom: '8px' }}>Top 5 Customers by Quote Value</div>
                      {renderOrderCustomerList(topCustomersByQuoteValue, 'No quote customer data available.')}
                    </div>

                    <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 4px 10px rgba(15, 23, 42, 0.05)', overflow: 'hidden' }}>
                      <div style={{ fontSize: '14px', color: '#475569', fontWeight: 600, marginBottom: '8px' }}>Top 5 Customers by Invoice Value</div>
                      {renderOrderCustomerList(topCustomersByInvoiceValue, 'No invoice customer data available.')}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 16px', color: '#0f172a' }}>Orders</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', margin: '-10px' }}>
                  {orderKpiStats.map((stat) => {
                    const periodChange = formatPeriodChange(stat.value, stat.previousValue);

                    return (
                      <div key={stat.label} style={statCardStyle}>
                        <div style={{ fontSize: '14px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '10px' }}>
                          {stat.label}
                        </div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#0f172a' }}>
                          {stat.format === 'currency' ? formatINR(stat.value) : stat.value.toLocaleString('en-IN')}
                        </div>
                        <div
                          style={{
                            marginTop: '8px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: periodChange.tone === 'negative'
                              ? '#b91c1c'
                              : periodChange.tone === 'positive'
                                ? '#15803d'
                                : '#64748b'
                          }}
                        >
                          {periodChange.text}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '20px' }}>
                  <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 4px 10px rgba(15, 23, 42, 0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                      <div style={{ fontSize: '14px', color: '#475569', fontWeight: 600 }}>Order Volume</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ color: '#64748b', fontSize: '12px', fontWeight: 600 }}>Group By:</span>
                        {[
                          { label: 'Day', value: 'daily' },
                          { label: 'Week', value: 'weekly' },
                          { label: 'Month', value: 'monthly' },
                          { label: 'Quarter', value: 'quarterly' },
                          { label: 'Year', value: 'yearly' }
                        ].map((option) => {
                          const active = orderChartView === option.value;
                          return (
                            <button
                              key={option.value}
                              type='button'
                              onClick={() => setOrderChartView(option.value)}
                              style={{
                                border: '1px solid',
                                borderColor: active ? '#121212' : '#cbd5e1',
                                backgroundColor: active ? '#121212' : 'white',
                                color: active ? 'white' : '#475569',
                                borderRadius: '999px',
                                padding: '6px 12px',
                                fontSize: '12px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                lineHeight: 1
                              }}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {orderVolumeData.length === 0 ? (
                      <div style={{ color: '#64748b' }}>No order volume data available for the selected filters.</div>
                    ) : (
                      <Column
                        data={orderVolumeData.flatMap((item) => [
                          { type: item.label, category: 'Rental', value: item.rental },
                          { type: item.label, category: 'Sales', value: item.sales }
                        ])}
                        isGroup={true}
                        xField='type'
                        yField='value'
                        seriesField='category'
                        colorField='category'
                        scale={{
                          color: {
                            domain: ['Rental', 'Sales'],
                            range: ['#8dc63f', '#121212']
                          }
                        }}
                        columnStyle={{ radius: [4, 4, 0, 0] }}
                        height={260}
                        meta={{
                          type: { alias: 'Period' },
                          value: { alias: 'Orders' },
                          category: { alias: 'Type' }
                        }}
                        label={{
                          position: 'top',
                          style: { fill: '#64748b', fontSize: 12 }
                        }}
                        xAxis={{
                          label: {
                            autoRotate: false,
                            style: { fill: '#64748b' }
                          }
                        }}
                        yAxis={{
                          label: {
                            style: { fill: '#64748b' }
                          }
                        }}
                      />
                    )}
                  </div>

                  <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 4px 10px rgba(15, 23, 42, 0.05)', overflow: 'hidden' }}>
                    <div style={{ fontSize: '14px', color: '#475569', fontWeight: 600, marginBottom: '12px' }}>Top 5 Customers by Order Value</div>
                    {renderOrderCustomerList(topOrderCustomers)}
                  </div>
                </div>

                <div style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '20px', alignItems: 'stretch' }}>
                  <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 4px 10px rgba(15, 23, 42, 0.05)', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <div style={{ fontSize: '14px', color: '#475569', fontWeight: 600, marginBottom: '12px' }}>Recent Orders</div>
                    <div style={{ flex: 1, minHeight: 0 }}>
                      {renderOrderCustomerList(recentOrders, 'No recent orders available.')}
                    </div>
                  </div>

                  <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 4px 10px rgba(15, 23, 42, 0.05)', overflow: 'hidden', minHeight: '220px', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontSize: '14px', color: '#475569', fontWeight: 600, marginBottom: '12px' }}>Order Volume by Salesperson</div>
                    <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
                      {topSalespersonsByOrderCount.length === 0 ? (
                        <div style={{ color: '#64748b' }}>No salesperson order data available.</div>
                      ) : (
                        <div style={{ flex: 1, minHeight: 0, width: '100%' }}>
                          <Column
                            data={topSalespersonsByOrderCount}
                            xField="salesperson"
                            yField="count"
                            color="#2b5f9e"
                            columnWidthRatio={0.95}
                            coordinate={{ transform: [{ type: 'transpose' }] }}
                            axis={{
                              x: {
                                title: false,
                                labelFill: '#374151',
                                labelFontSize: 12
                              },
                              y: {
                                position: 'bottom',
                                title: false,
                                labelFormatter: (value) => Number(value).toLocaleString('en-IN'),
                                labelFill: '#374151',
                                labelFontSize: 12,
                                grid: true,
                                gridStroke: '#e5e7eb',
                                gridLineWidth: 1
                              }
                            }}
                            tooltip={{
                              title: (datum) => datum.salesperson,
                              items: [
                                {
                                  field: 'count',
                                  name: 'Orders',
                                  valueFormatter: (value) => `${Number(value).toLocaleString('en-IN')} orders`
                                }
                              ]
                            }}
                            height={220}
                            padding="auto"
                            autoFit
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0, height: '100%' }}>
                    <div style={{ fontSize: '14px', color: '#475569', fontWeight: 600, whiteSpace: 'nowrap' }}>Salesperson Leaderboard (By order value)</div>
                    <div style={{ background: 'white', padding: '16px', borderRadius: '12px', boxShadow: '0 4px 10px rgba(15, 23, 42, 0.05)', minWidth: 0, overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                      {topSalespersonsByOrderValue.length === 0 ? (
                        <div style={{ color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                          No salesperson data available for the selected period.
                        </div>
                      ) : (
                        <div style={{ flex: 1, minHeight: 0, width: '100%' }}>
                          <Column
                            data={topSalespersonsByOrderValue}
                            xField="salesperson"
                            yField="amount"
                            color="#2b5f9e"
                            columnWidthRatio={0.95}
                            coordinate={{ transform: [{ type: 'transpose' }] }}
                            axis={{
                              x: {
                                title: false,
                                labelFill: '#374151',
                                labelFontSize: 12
                              },
                              y: {
                                position: 'bottom',
                                title: false,
                                labelFormatter: (value) => formatCompactINR(value),
                                labelFill: '#374151',
                                labelFontSize: 12,
                                grid: true,
                                gridStroke: '#e5e7eb',
                                gridLineWidth: 1
                              }
                            }}
                            tooltip={{
                              title: (datum) => datum.salesperson,
                              items: [
                                {
                                  field: 'amount',
                                  name: 'Order Value',
                                  valueFormatter: (value) => formatCompactINR(value)
                                }
                              ]
                            }}
                            height={280}
                            padding="auto"
                            autoFit
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '20px', background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 4px 10px rgba(15, 23, 42, 0.05)', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: '14px', color: '#475569', fontWeight: 600, marginBottom: '12px' }}>Order Status</div>
                  {orderStatusData.length === 0 ? (
                    <div style={{ color: '#64748b' }}>No order data available for the selected period.</div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'nowrap', overflowX: 'auto', margin: '-10px', alignItems: 'stretch' }}>
                      {orderStatusData.map((item) => (
                        <div key={item.status} style={{ ...statCardStyle, minWidth: '180px', flex: '0 0 auto' }}>
                          <div style={{ fontSize: '14px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '10px' }}>
                            {item.status}
                          </div>
                          <div style={{ fontSize: '28px', fontWeight: 700, color: '#0f172a' }}>
                            {item.count.toLocaleString('en-IN')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;


